import { metadata as rootMetadata, SITE_URL } from "../app/layout";
import type { Metadata } from "./types";

function setMeta(attribute: "name" | "property", key: string, content: string | null) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (content === null) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(href: string | null) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
}

function resolveTitle(title: Metadata["title"]) {
  const root = rootMetadata.title as { default: string; template: string };
  if (!title) return root.default;
  if (typeof title === "string") return root.template.replace("%s", title);
  return title.absolute ?? title.default ?? root.default;
}

function resolveRobots(robots: Metadata["robots"]) {
  if (!robots) return null;
  if (typeof robots === "string") return robots;
  return `${robots.index === false ? "noindex" : "index"}, ${robots.follow === false ? "nofollow" : "follow"}`;
}

// Applies a page's metadata the way the Next.js root layout template would.
export function applyMetadata(page: Metadata | undefined) {
  document.title = resolveTitle(page?.title);
  setMeta("name", "description", page?.description ?? (rootMetadata.description as string));
  setMeta("name", "robots", resolveRobots(page?.robots ?? rootMetadata.robots));

  const canonical = (page?.alternates as { canonical?: string } | undefined)?.canonical;
  setCanonical(canonical ? new URL(canonical, SITE_URL).toString() : null);
}
